import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Button,
  Dialog,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { useSnackbar } from "notistack";
import { useSendBulkEmail, uploadEmailAttachment } from "../../hooks/usePlanSummary";
import { useEmailCampaign } from "../../hooks/useEmail";

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

let attachmentSeq = 0;

// Picked purely from the filename extension — this is a visual hint, not a security check,
// so a quick guess is fine even if a renamed file makes it briefly wrong.
function fileTypeIcon(filename) {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return <PictureAsPdfRoundedIcon sx={{ fontSize: 22, color: "#D93025" }} />;
  if (["doc", "docx"].includes(ext)) return <DescriptionRoundedIcon sx={{ fontSize: 22, color: "#1A73E8" }} />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <TableChartRoundedIcon sx={{ fontSize: 22, color: "#188038" }} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: "#5F6368" }} />;
}

// One attached file's row — mirrors Gmail compose's own attachment chips: a real thumbnail
// for images (from a local blob URL, so it shows instantly without waiting on the upload),
// a file-type icon otherwise, filename + size, a spinner while uploading (POST
// /:planId/email-attachments happens immediately on pick, not deferred to Send), and a
// remove (×) button once resolved either way. Clicking the preview opens the file — the
// local blob while still uploading, the real S3 url once done — so "preview" means an
// actual look at the file, not just its name.
function AttachmentRow({ attachment, onRemove }) {
  const isImage = attachment.contentType?.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(attachment.filename || "");
  const previewUrl = attachment.url || attachment.localPreviewUrl;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: "8px",
        px: 1.25,
        py: 0.75,
        bgcolor: attachment.status === "error" ? "#FDECEA" : "#F8F9FA",
        maxWidth: 260,
      }}
    >
      <Box
        onClick={() => previewUrl && window.open(previewUrl, "_blank", "noopener,noreferrer")}
        sx={{
          width: 34,
          height: 34,
          borderRadius: "6px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          bgcolor: isImage && previewUrl ? "transparent" : "rgba(0,0,0,0.04)",
          cursor: previewUrl ? "pointer" : "default",
        }}
      >
        {attachment.status === "uploading" && !isImage ? (
          <CircularProgress size={16} thickness={5} />
        ) : attachment.status === "error" ? (
          <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: "#D93025" }} />
        ) : isImage && previewUrl ? (
          <Box component="img" src={previewUrl} alt={attachment.filename} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          fileTypeIcon(attachment.filename)
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 12.5, fontWeight: 600, color: "#202124" }}>
          {attachment.filename}
        </Typography>
        <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 10.5, color: attachment.status === "error" ? "#D93025" : "#80868B" }}>
          {attachment.status === "error" ? attachment.error || "Upload failed" : attachment.status === "uploading" ? `Uploading… ${attachment.progress || 0}%` : formatBytes(attachment.size)}
        </Typography>
      </Box>
      <IconButton size="small" onClick={() => onRemove(attachment.id)} sx={{ p: 0.4 }}>
        <CloseRoundedIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Stack>
  );
}

// Email sibling of WhatsAppBulkSendModal.jsx — same dialog shape, but free-compose
// (subject + message) instead of a picked template, since email has no Meta-style
// approval gate to choose from. Sends from the caller's own idiotic.media mailbox
// (domain-wide delegation, see pluginbackendnode/helpers/googleServiceAccountAuth.js), so
// unlike the WhatsApp modal there's no number/opt-in step — every idiotic.media-domain
// staff member can send immediately. Under the hood this creates (and runs) a manual-
// audience EmailCampaign — see useEmailCampaign polling below — so the send also shows up
// with full history on the Email Campaigns page, same as the WhatsApp modal's own send
// tracking as a WhatsappCampaign.
//
// Styled after Gmail's own compose window rather than a generic form dialog: borderless
// Subject/Message fields, a Send pill + attach-file icon in the footer, and attachment
// chips uploaded immediately on pick (not deferred to Send) — see AttachmentRow above.
function EmailBulkSendModal({ open, onClose, planId, selectedIds }) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const sendMutation = useSendBulkEmail(planId);
  const fileInputRef = useRef(null);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [runId, setRunId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: campaignData } = useEmailCampaign(campaignId, { poll: Boolean(campaignId) });
  const run = (campaignData?.campaign?.runs || []).find((r) => r._id === runId) || null;

  const recipientCount = selectedIds.size;
  const uploadingCount = attachments.filter((a) => a.status === "uploading").length;
  const readyAttachments = attachments.filter((a) => a.status === "done");

  const resetForm = () => {
    attachments.forEach((a) => a.localPreviewUrl && URL.revokeObjectURL(a.localPreviewUrl));
    setSubject("");
    setMessage("");
    setAttachments([]);
    setCampaignId(null);
    setRunId(null);
  };

  const handleClose = () => {
    // Only the synchronous POST that mints campaignId/runId (`submitting`) blocks closing —
    // once that's back, the send itself runs in the backend queue regardless of whether
    // this dialog stays open, so there's nothing to wait for. Previously also blocked on
    // `isInFlight` (still queued/running), which meant a worker crash or dropped status
    // update left this dialog permanently un-closable, with no cancel or timeout escape.
    if (submitting) return;
    resetForm();
    onClose();
  };

  const handlePickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // lets picking the same file twice re-trigger onChange
    if (!files.length) return;

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      enqueueSnackbar(`You can attach up to ${MAX_ATTACHMENTS} files`, { variant: "warning" });
      return;
    }

    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        enqueueSnackbar(`${file.name} is over ${formatBytes(MAX_ATTACHMENT_BYTES)} — skipped`, { variant: "warning" });
        continue;
      }

      const id = ++attachmentSeq;
      // Local-only preview URL — shows instantly for images without waiting on the
      // upload, and still works as the click-to-open preview if the upload is slow.
      // Revoked on remove/reset (see handleRemoveAttachment/resetForm) — it's only ever
      // read from the browser's own memory, never sent anywhere.
      const localPreviewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
      setAttachments((prev) => [
        ...prev,
        { id, filename: file.name, size: file.size, contentType: file.type, status: "uploading", progress: 0, localPreviewUrl },
      ]);

      uploadEmailAttachment(planId, file, (progress) => {
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, progress } : a)));
      })
        .then((res) => {
          setAttachments((prev) =>
            prev.map((a) => (a.id === id ? { ...a, status: "done", url: res.url, contentType: res.contentType, size: res.size } : a))
          );
        })
        .catch((error) => {
          setAttachments((prev) =>
            prev.map((a) => (a.id === id ? { ...a, status: "error", error: error?.response?.data?.message || "Upload failed" } : a))
          );
        });
    }
  };

  const handleRemoveAttachment = (id) => {
    setAttachments((prev) => {
      const found = prev.find((a) => a.id === id);
      if (found?.localPreviewUrl) URL.revokeObjectURL(found.localPreviewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSend = async () => {
    const ids = Array.from(selectedIds);
    if (!subject.trim() || !message.trim() || !ids.length || uploadingCount > 0) return;

    setSubmitting(true);
    try {
      const result = await sendMutation.mutateAsync({
        influencerIds: ids,
        subject: subject.trim(),
        message: message.trim(),
        attachments: readyAttachments.map((a) => ({ filename: a.filename, url: a.url, contentType: a.contentType, size: a.size })),
      });
      setCampaignId(result.campaignId);
      setRunId(result.runId);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to send email", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const sent = run?.sent || [];
  const failed = run?.failed || [];
  const isDone = run?.status === "completed" || run?.status === "failed";

  // Email sends are queued, so the plan table's Email Status column only has something new
  // once the run finishes — refetch then, not on enqueue.
  useEffect(() => {
    if (isDone) queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
  }, [isDone, planId, queryClient]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MailOutlineIcon sx={{ color: "#1A73E8", fontSize: 22 }} />
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 16, color: "#202124" }}>
            {campaignId ? "Email" : `Send email to ${recipientCount} influencer${recipientCount === 1 ? "" : "s"}`}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={handleClose} disabled={submitting}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5 }}>
        {!campaignId ? (
          <>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 12, color: "#80868B", mb: 1.5 }}>
              Sent from your own idiotic.media address — the influencer will see it come from you, not a shared account.
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              InputProps={{ disableUnderline: false }}
              sx={{
                mb: 0.5,
                "& .MuiInput-root": { fontFamily: "Quicksand", fontSize: 14.5, fontWeight: 600, color: "#202124" },
                "& .MuiInput-underline:before": { borderBottomColor: "rgba(0,0,0,0.1)" },
              }}
            />
            <TextField
              fullWidth
              variant="standard"
              multiline
              minRows={7}
              maxRows={12}
              placeholder="Compose your message…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              InputProps={{ disableUnderline: true }}
              sx={{ mt: 1.5, "& .MuiInput-root": { fontFamily: "Quicksand", fontSize: 13.5, color: "#3C4043", lineHeight: 1.6 } }}
            />
            {attachments.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1, mb: 1 }}>
                {attachments.map((a) => (
                  <AttachmentRow key={a.id} attachment={a} onRemove={handleRemoveAttachment} />
                ))}
              </Stack>
            )}
          </>
        ) : !isDone ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1.5 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 13, color: "#6B7280" }}>Sending…</Typography>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: "#9CA3AF" }}>
              Safe to close — this keeps sending in the background.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {run.status === "failed" && sent.length === 0 && (
              <Alert severity="error">Something went wrong sending this batch — try again.</Alert>
            )}
            {sent.length > 0 && (
              <Alert severity="success">
                Sent to {sent.length} influencer{sent.length === 1 ? "" : "s"}
                {readyAttachments.length > 0 ? ` with ${readyAttachments.length} attachment${readyAttachments.length === 1 ? "" : "s"}` : ""}.
              </Alert>
            )}
            {failed.length > 0 && (
              <Alert severity="warning">
                {failed.length} failed:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {failed.map((f) => (
                    <li key={f.influencerId}>
                      {f.name} — {f.reason}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 12.5, color: "#6B7280" }}>
              This send is tracked as an Email Campaign — see full history on the{" "}
              <a href="/email/campaigns" target="_blank" rel="noreferrer">Email Campaigns</a> page.
            </Typography>
          </Stack>
        )}
      </Box>

      {!campaignId && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.75, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              onClick={handleSend}
              variant="contained"
              disabled={!subject.trim() || !message.trim() || submitting || uploadingCount > 0}
              endIcon={submitting ? null : <SendRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                boxShadow: "none",
                px: 3,
                fontFamily: "Quicksand",
                fontWeight: 700,
                bgcolor: "#1A73E8",
                "&:hover": { bgcolor: "#1A66D0", boxShadow: "none" },
              }}
            >
              {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send"}
            </Button>
            <Tooltip title={uploadingCount > 0 ? "Waiting for uploads to finish" : "Attach files"}>
              <span>
                <IconButton onClick={() => fileInputRef.current?.click()} disabled={submitting}>
                  <AttachFileRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <input ref={fileInputRef} type="file" multiple hidden onChange={handlePickFiles} />
          </Stack>
          <Tooltip title="Discard">
            <span>
              <IconButton onClick={handleClose} disabled={submitting}>
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}
      {campaignId && (
        <Stack direction="row" justifyContent="flex-end" sx={{ px: 2.5, py: 1.75, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <Button onClick={handleClose} sx={{ textTransform: "none", borderRadius: "20px", fontFamily: "Quicksand", fontWeight: 700 }}>
            Close
          </Button>
        </Stack>
      )}
    </Dialog>
  );
}

export default EmailBulkSendModal;
